/**
 * ターゲット語選定アルゴリズム（Prosodyファースト × 一点突破）
 * 
 * 仕様：
 * - Prosody < 75 または (Prosody 75-84 かつ Fluency < 70) → 流れ優先
 * - それ以外 → 語矯正優先
 * - 優先度が低いほど優先度が高い（選ばれやすい）
 */

/**
 * ゲートタイプ判定
 * @param {number} prosody - ProsodyScore (0-100)
 * @param {number} fluency - FluencyScore (0-100)
 * @returns {'flow_priority' | 'word_priority'}
 */
export function getDrillGate(prosody, fluency) {
  // ① Prosody < 75 → 流れ優先
  if (prosody < 75) {
    return 'flow_priority'
  }
  
  // ② Prosody 75-84 かつ Fluency < 70 → 流れ優先
  if (prosody >= 75 && prosody < 85 && fluency < 70) {
    return 'flow_priority'
  }
  
  // ③ 上記以外 → 語矯正優先
  return 'word_priority'
}

/**
 * ターゲット語を選定（簡素化：語スコアが最も低い語を選ぶ）
 * @param {Object} evaluationResult - Azure評価結果（標準化済み）
 * @param {Object} userProfile - ユーザープロファイル（未使用だが互換性のため保持）
 * @returns {Object} { targetWord, gateType, priority }
 */
export function selectTargetWord(evaluationResult, userProfile = {}) {
  const { words = [], missingWords = [] } = evaluationResult
  
  // 候補語収集（認識語 + 脱落語）
  const candidates = []
  
  // 認識語を候補に追加
  for (const word of words) {
    candidates.push({
      ...word,
      score: word.score || 0,
      isMissing: false
    })
  }
  
  // 脱落語（ゴースト）を候補に追加（スコア0として扱う）
  for (const missingWord of missingWords) {
    candidates.push({
      word: missingWord.expectedWord || missingWord.word,
      expectedWord: missingWord.expectedWord,
      score: 0, // 脱落語はスコア0
      errorType: 'deletion',
      offset: missingWord.position || 0,
      duration: 0,
      isMissing: true
    })
  }
  
  // 語スコアが最も低い語を選定（スコアが同じ場合は最初の語）
  if (candidates.length === 0) {
    return {
      targetWord: null,
      gateType: 'word_priority',
      priority: null
    }
  }
  
  // スコアでソート（低い順）
  candidates.sort((a, b) => (a.score || 0) - (b.score || 0))
  
  // 最優先語を選定
  const targetWord = candidates[0]
  
  return {
    targetWord,
    gateType: 'word_priority', // 常に語矯正優先
    priority: targetWord?.score || null
  }
}

/**
 * 単語の優先度を計算（低いほど優先度が高い）
 * @param {Object} word - 単語データ
 * @param {Object} evaluationResult - 評価結果全体
 * @param {Object} userProfile - ユーザープロファイル
 * @param {Object} prosodyDetails - Prosody詳細情報
 * @returns {number} 優先度（低いほど選ばれやすい）
 */
function calculateWordPriority(word, evaluationResult, userProfile, prosodyDetails) {
  // base優先度 = 100 - word.score（スコアが低いほど優先度UP）
  let priority = 100 - (word.score || 0)
  
  // 補正値（マイナスで優先度UP）
  
  // 直前直後に Missing/Unexpected Break
  if (hasBreakBeforeOrAfter(word, prosodyDetails)) {
    priority -= 10
  }
  
  // 強勢語/強勢音節
  if (word.isStressed || isStressedSyllableInWord(word, prosodyDetails)) {
    priority -= 8
  }
  
  // 履歴ベース弱音素
  if (userProfile.weakPhonemes && hasWeakPhoneme(word, userProfile.weakPhonemes)) {
    priority -= 6
  }
  
  // 頻出語
  if (userProfile.frequentWords && userProfile.frequentWords.includes(word.word?.toLowerCase())) {
    priority -= 4
  }
  
  // 機能語（後回し）
  if (isFunctionWord(word.word)) {
    priority += 2
  }
  
  return Math.max(0, priority) // 負の値は防ぐ
}

/**
 * タイブレーク値取得（音節少 < 置換 < 脱落 < 挿入）
 * @param {string|null} errorType - エラー種別
 * @param {number} syllableCount - 音節数
 * @returns {number} タイブレーク値（小さいほど優先）
 */
function getTiebreakerValue(errorType, syllableCount) {
  // 音節少: 0-9, 置換: 10-19, 脱落: 20-29, 挿入: 30-39
  const base = syllableCount || 1
  
  if (errorType === 'substitution') {
    return 10 + base
  } else if (errorType === 'deletion') {
    return 20 + base
  } else if (errorType === 'insertion') {
    return 30 + base
  }
  
  // エラーなし（OK）: 音節数ベース
  return base
}

/**
 * Break（Missing/Unexpected）が直前直後にあるか
 */
function hasBreakBeforeOrAfter(word, prosodyDetails) {
  if (!prosodyDetails.breaks || !word.offset) return false
  
  const wordStart = word.offset / 10000000 // 秒
  const wordEnd = (word.offset + word.duration) / 10000000
  
  // 直前直後50ms以内にBreakがあるか
  const threshold = 0.05
  
  for (const breakPoint of prosodyDetails.breaks) {
    const breakTime = breakPoint.position / 10000000
    
    if (Math.abs(breakTime - wordStart) < threshold || 
        Math.abs(breakTime - wordEnd) < threshold) {
      return true
    }
  }
  
  return false
}

/**
 * 強勢音節が単語内にあるか
 */
function isStressedSyllableInWord(word, prosodyDetails) {
  if (!prosodyDetails.stress || !word.offset) return false
  
  const wordStart = word.offset / 10000000
  const wordEnd = (word.offset + word.duration) / 10000000
  
  for (const stress of prosodyDetails.stress) {
    const stressTime = stress.offset / 10000000
    
    if (stressTime >= wordStart && stressTime <= wordEnd && 
        (stress.level === 'primary' || stress.level === 'secondary')) {
      return true
    }
  }
  
  return false
}

/**
 * 弱音素を含むか（履歴ベース）
 */
function hasWeakPhoneme(word, weakPhonemes) {
  if (!word.phonemes || !Array.isArray(word.phonemes)) return false
  
  for (const phoneme of word.phonemes) {
    if (weakPhonemes.includes(phoneme.phoneme)) {
      return true
    }
  }
  
  return false
}

/**
 * 機能語かどうか
 */
function isFunctionWord(word) {
  if (!word) return false
  
  const functionWords = [
    'a', 'an', 'the', 'and', 'or', 'but', 'of', 'to', 'in', 'on', 'at', 
    'for', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'be', 
    'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 
    'could', 'should', 'may', 'might', 'can', 'this', 'that', 'these', 
    'those', 'it', 'he', 'she', 'we', 'they', 'you', 'I', 'my', 'your', 
    'his', 'her', 'our', 'their'
  ]
  
  return functionWords.includes(word.toLowerCase())
}

/**
 * 音節数を取得（簡易版：母音の数で推定）
 */
function getSyllableCount(word) {
  if (!word.phonemes || !Array.isArray(word.phonemes)) {
    // フォールバック：単語から推定
    return getSyllableCountFromWord(word.word || '')
  }
  
  // 母音音素をカウント
  const vowels = ['a', 'e', 'i', 'o', 'u', 'ɪ', 'ɛ', 'æ', 'ɑ', 'ɔ', 'ʊ', 
                  'ə', 'ʌ', 'aɪ', 'aʊ', 'oɪ', 'eɪ', 'oʊ']
  
  return word.phonemes.filter(p => 
    vowels.some(v => p.phoneme?.toLowerCase().includes(v))
  ).length || 1
}

/**
 * 単語文字列から音節数を推定（簡易版）
 */
function getSyllableCountFromWord(word) {
  if (!word) return 1
  
  const lowerWord = word.toLowerCase().trim()
  if (lowerWord.length <= 3) return 1
  
  // 母音をカウント（簡易版）
  const vowelMatches = lowerWord.match(/[aeiouy]+/gi)
  return vowelMatches ? vowelMatches.length : 1
}

